import { useLazyQuery } from '@oneaudi/onegraph-client';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Calculator } from '~/@types/calculator';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useMessageDialog } from '~/components/dialog/message-dialog/message-dialog';

import { useAppState } from '~/app/contexts/app-context';
import { PossibleConfiguratorOptionValues } from '~/app/models/configurator-options';
import { ImageViewsListWithENV } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { ErrorType } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { NgcCarIdentifier } from '~/app/utils/graphql-sub-types';
import { normalizeCarIdentifier } from '~/app/utils/normalizeCarIdentifier';
import { Category, extractCategories, GroupedAccessories } from '~/graphql/mapper/map-accessories';
import { mapSelection } from '~/graphql/mapper/map-selection';
import { state } from '~/storage';
import { conflictAtom, getInitialLoadingStateMap, loadingFeatureIdsAtom } from '~/storage/state';
import { windowAvailable } from '~/utils/client-detection';
import { TECHNOLOGY_PACKAGES } from '~/utils/family-constants';
import {
	AcceptConflictSolutionQuery,
	ConfiguredCarEssentialsFragment,
	ConfiguredCarFeatureFragment,
	ConfiguredCarInput,
	ConfiguredCarPackageFragment,
	RenderedCarImageFragment,
	SelectedOptionsDocument,
	Status,
} from '../__generated__/graphql';
import { CheckConflictResult, handleConfigurationChange } from '../conflicts';
import { mapAccessories, mapCarline, mapCatalog } from '../mapper';
import { mapPriceSummary } from '../mapper/map-prices';
import { CatalogItemType, CombinedCatalog, FeatureCategory, LoadingFlags, SelectableOption } from '../types';
import {
	imageSetFilter,
	useAccessoriesQuery,
	useCarlineQuery,
	useCatalogQuery,
	useCheckConflicts,
	usePriceSummaryQuery,
} from './use-configured-car-queries';

export type ConfiguratorContextType = {
	carIdentifier: NgcCarIdentifier;
	/** Replaces the current carIdentifier and reloads the corresponding configurator data. */
	setCarIdentifier: (carIdentifier: NgcCarIdentifier) => Promise<void>;
	/** Accepts a conflict solution and reloads configurator data. */
	acceptConflictSolution: (data: AcceptConflictSolutionQuery['acceptConflictSolutionsByUrl']) => Promise<void>;
	catalog: Partial<CombinedCatalog>;
	accessories: {
		exclusive: GroupedAccessories;
		inclusive: GroupedAccessories;
	};
	/**
	 * Contains information about the loading state of the configurator.
	 */
	loadingFlags: LoadingFlags;
	/**
	 * Determines, whether the initial configurator data is loading.
	 */
	isLoading: boolean;
	/** Determines, whether the given item has been selected by the user and is now in a loading state. */
	isItemLoading: (item: { type: FeatureCategory; featureId: string }) => boolean;
	/** Determines, whether an item should be displayed as currently selected. */
	isItemChecked: (item: { type: FeatureCategory; featureId: string; status: Status | null | undefined }) => boolean;
	/** Changes the configuration by adding/removing the given item. */
	changeConfiguration: (
		item: SelectableOption<FeatureCategory>,
		CarIdentifier?: NgcCarIdentifier,
	) => Promise<undefined | NgcCarIdentifier>;
	didConfigChange: boolean;
	financialCalculator: Calculator | undefined;
};

export const ConfiguratorContext = createContext<ConfiguratorContextType>(null as unknown as ConfiguratorContextType);

/** Special PR String for 3D rendering. */
export const aveRenderStringAtom = atom<string | undefined>(undefined);
export const imagesAtom = atom<RenderedCarImageFragment[]>([]);

const selectionAtom = atom(new Set<string>());

export function ConfiguratorProvider({ children }: { children: React.ReactNode }) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const [carIdentifier, setCarIdentifier] = useState(config.carIdentifier);
	const [didConfigChange, setDidConfigChange] = useState(false);
	const [, setAllImages] = useAtom(imagesAtom);
	const [, setCarType] = useAtom(state.carType);
	const [, setAveRenderString] = useAtom(aveRenderStringAtom);
	const { catalog, isLoadingCatalog } = useCatalogQuery(carIdentifier, config.packagesFilter);
	const { priceSummary, isLoadingPriceSummary } = usePriceSummaryQuery(carIdentifier);
	const { financingData } = useAppState();

	const [, setConsumptionDisclaimersConfiguredCar] = useAtom(state.consumptionDisclaimersConfiguredCarAtom);

	const { accessories, isLoadingAccessories } = useAccessoriesQuery(carIdentifier);

	const { carline, isLoadingCarline } = useCarlineQuery(carIdentifier, config.financingInput);
	const {
		checkConflictAfterFeatureChange,
		checkConflictAfterModelChange,
		checkConflictAfterIncentiveChange,
		isCheckingConflicts,
	} = useCheckConflicts();

	const [initialLoading, setInitialLoading] = useState(true);
	const [loadingFeatureIds, setLoadingFeatureIds] = useAtom(loadingFeatureIdsAtom);
	const setConflict = useSetAtom(conflictAtom);
	const [selection, setSelection] = useAtom(selectionAtom);
	const { showMessageDialog } = useMessageDialog();
	const { isDebug } = useDebugMode();

	const [loadInitialSelection] = useLazyQuery(SelectedOptionsDocument, {
		fetchPolicy: 'no-cache',
		returnPartialData: true,
		variables: {
			configuredCar: {} as ConfiguredCarInput,
			filterInput: { filterByProfileId: ImageViewsListWithENV },
		},
	});

	const setEssentialConfigurationData = useCallback(
		(response: ConfiguredCarEssentialsFragment) => {
			setCarIdentifier(normalizeCarIdentifier(response.id));
			if (response.catalog) {
				setSelection(mapSelection(response.catalog, response.id.model.code));
			} else {
				console.error('Catalog is missing');
			}
			if (response.media?.images?.length) {
				setAllImages((response.media?.images as RenderedCarImageFragment[]) ?? []);
			}
			setAveRenderString(response.media?.ave?.renderString ?? undefined);
		},
		[setCarIdentifier, setSelection, setAllImages, setAveRenderString],
	);

	useEffect(() => {
		setCarIdentifier(normalizeCarIdentifier(config.carIdentifier));
	}, [JSON.stringify(config.carIdentifier)]);

	const [initialSelectionLoading, setInitialSelectionLoading] = useState(true);
	useEffect(() => {
		loadInitialSelection({
			variables: {
				configuredCar: carIdentifier,
			},
			onCompleted: (data) => {
				setEssentialConfigurationData(data.configuredCar);
				setInitialSelectionLoading(false);
			},
		});
	}, []);

	useEffect(() => {
		if (initialLoading && !isLoadingCatalog && !isLoadingCarline && !initialSelectionLoading) {
			setInitialLoading(false);
		}
	}, [initialLoading, isLoadingCatalog, isLoadingCarline, initialSelectionLoading]);

	const priceSummaryMapped = useMemo(() => {
		if (!priceSummary) return undefined;
		return mapPriceSummary(priceSummary, config, financingData?.financing?.rate);
	}, [priceSummary, config, financingData?.financing?.rate]);

	const { carInfo, trimlines } = useMemo(() => {
		if (!carline) return {} as Partial<ReturnType<typeof mapCarline>>;
		const disclaimers = carline?.configuredCar?.technicalDataV2?.consumptionAndEmission?.disclaimers.map(
			(disclaimer) => disclaimer.value,
		);
		const carType = carline?.configuredCar?.carlineStructureCarline?.vehicleType;
		if (carType) setCarType(carType);
		if (disclaimers) {
			setConsumptionDisclaimersConfiguredCar(disclaimers);
		}
		return mapCarline(carline, config, isDebug);
	}, [carline, config, isDebug]);

	const catalogOptions = useMemo(() => {
		if (!catalog) return {} as Partial<ReturnType<typeof mapCatalog>>;
		return mapCatalog(catalog, config);
	}, [catalog, config]);

	const mappedAccessories = useMemo(() => {
		if (!accessories) return {} as Partial<ReturnType<typeof mapAccessories>>;
		return mapAccessories(accessories, config);
	}, [accessories, config]);

	const { accessoriesExclusive, accessoriesInclusive } = useMemo(() => {
		if (!accessories || !catalogOptions) {
			return {
				accessoriesExclusive: new Map<string, Category>(),
				accessoriesInclusive: new Map<string, Category>(),
			};
		}

		const visibleItems: string[] = Object.keys(catalogOptions).flatMap((catalogItemId: string) => {
			return (catalogOptions[catalogItemId as keyof typeof catalogOptions] || [])
				.map((item) => {
					if (typeof item === 'string') return;
					return item.pr7;
				})
				.filter(Boolean) as string[]; // Assert that the filtered array contains only strings
		});

		const filterAccessories = (item: ConfiguredCarFeatureFragment | ConfiguredCarPackageFragment) => {
			const itemIsNotShowInOptions = visibleItems.findIndex((pr7) => item.pr7 === pr7) === -1;
			const itemIsAvailable = item.status.available;
			const itemIsNoPartOfTechPackage = item.family.id !== TECHNOLOGY_PACKAGES;
			const hideStandardItem = !config.hideStandardAccessories || !item.status.standard;
			return itemIsAvailable && itemIsNotShowInOptions && hideStandardItem && itemIsNoPartOfTechPackage;
		};

		const accExclusive = accessories.configuredCar.accessoriesExclusive
			? extractCategories(
					accessories.configuredCar.accessoriesExclusive,
					config.showPrices,
					!config.hideMonthlyRates,
					filterAccessories,
				)
			: new Map<string, Category>();
		accExclusive.delete('Weitere');
		accExclusive.delete('Interior_lines');
		accExclusive.delete('Linien_Pakete');

		return {
			accessoriesExclusive: accExclusive,
			accessoriesInclusive: accessories.configuredCar.accessoriesInclusive
				? extractCategories(
						accessories.configuredCar.accessoriesInclusive,
						config.showPrices,
						!config.hideMonthlyRates,
						(item) => !config.hideStandardAccessories || !item.status.standard,
					)
				: new Map<string, Category>(),
		};
	}, [accessories, catalogOptions, config.hideMonthlyRates, config.hideStandardAccessories, config.showPrices]);

	const changeConfiguration = useCallback(
		async (
			item: Parameters<ConfiguratorContextType['changeConfiguration']>[0],
			carIdentifierOverwrite?: NgcCarIdentifier,
		) => {
			if (isCheckingConflicts) {
				console.warn(
					"Can't change configuration while checking for conflicts. Selection of the following item has been ignored: ",
					item,
				);
				return;
			}

			if (!item.status?.changeable) {
				console.warn(
					'The selected item is not changeable. Choosing an equivalent item will (probably) replace it.',
					item,
				);

				showMessageDialog({
					title: i18n('ngc.errors.notChangeable.title'),
					message: i18n('ngc.errors.notChangeable.message', { itemName: item.name }),
				});
				appTrackingService.trackError({
					name: ErrorType.ITEM_SELECTION,
					userMessage: i18n('ngc.errors.notChangeable.message', { itemName: item.name }),
				});
				return;
			}
			let action: 'ADD' | 'REMOVE';
			const clientSideOverwrite = loadingFeatureIds[item.type][item.featureId];
			if (item.type === 'trimline' || item.type === 'driveTrain' || item.type === 'bodyPaint') {
				action = 'ADD';
			} else if (clientSideOverwrite) {
				action = clientSideOverwrite.checked ? 'REMOVE' : 'ADD';

				console.info('Client side overwrite detected:', {
					serverSide: item.status?.selected,
					clientSide: clientSideOverwrite.checked,
					action,
					item,
				});
				setLoadingFeatureIds(getInitialLoadingStateMap());
			} else {
				action = selection.has(item.featureId) ? 'REMOVE' : 'ADD';
			}

			let result: CheckConflictResult | undefined;
			if (item.type === 'driveTrain') {
				const driveTrain = item as CatalogItemType['driveTrain'];

				result = await handleConfigurationChange({
					type: 'model',
					checkConflict: checkConflictAfterModelChange,
					variables: {
						input: {
							id: normalizeCarIdentifier(carIdentifierOverwrite ?? carIdentifier),
							model: {
								code: driveTrain.modelId.code,
								year: driveTrain.modelId.year,
								version: driveTrain.modelId.version,
								extensions: driveTrain.modelId.extensions,
							},
						},
						imageTypeFilterInput: imageSetFilter,
						filterInput: { filterByProfileId: ImageViewsListWithENV },
					},
				});
			} else if (item.type === 'incentive') {
				result = await handleConfigurationChange({
					type: 'incentive',
					checkConflict: checkConflictAfterIncentiveChange,
					variables: {
						input: {
							id: normalizeCarIdentifier(carIdentifierOverwrite ?? carIdentifier),
							feature: item.featureId,
							action: item.status.selected ? 'REMOVE' : 'ADD',
						},
						imageTypeFilterInput: imageSetFilter,
						filterInput: { filterByProfileId: ImageViewsListWithENV },
					},
				});

				setLoadingFeatureIds((prev) => {
					const next = { ...prev };

					next[item.type][item.featureId] = {
						...next[item.type][item.featureId],
						loading: false,
					};

					return next;
				});
			} else {
				result = await handleConfigurationChange({
					type: 'feature',
					checkConflict: checkConflictAfterFeatureChange,
					variables: {
						input: {
							id: normalizeCarIdentifier(carIdentifierOverwrite ?? carIdentifier),
							feature: item.featureId,
							action,
						},
						imageTypeFilterInput: imageSetFilter,
						filterInput: { filterByProfileId: ImageViewsListWithENV },
					},
				});
			}

			if (!result) {
				throw new Error('result is undefined.');
			}

			if (result.status === 'success') {
				if (item.type !== 'incentive') {
					setDidConfigChange(true);
				}

				setLoadingFeatureIds((prev) => {
					const next = { ...prev };

					next[item.type][item.featureId] = {
						checked: action === 'ADD',
						loading: true,
					};

					return next;
				});
				setEssentialConfigurationData(result.data);
			} else if (result.status === 'conflict') {
				setConflict(result.conflict);
			} else if (result.status === 'error') {
				console.error('Error while checking for conflicts.');

				showMessageDialog({
					title: i18n('ngc.errors.unexpected.title'),
					message: i18n('ngc.errors.unexpected.message'),
				});

				appTrackingService.trackError({
					name: ErrorType.UNEXPECTED_CONFIGURATION_ERROR,
					userMessage: i18n('ngc.errors.unexpected.message'),
				});
			}

			setLoadingFeatureIds(getInitialLoadingStateMap());

			// Returns the new car identifier, needed in some special cases when multiple updates for configuration are triggered right after.
			// If not used, the old car identifier is used which results into outdated data.
			if (result.status === 'success') {
				return result.data.id;
			}
		},
		[
			isCheckingConflicts,
			loadingFeatureIds,
			setLoadingFeatureIds,
			showMessageDialog,
			i18n,
			selection,
			checkConflictAfterModelChange,
			carIdentifier,
			config.financingInput,
			checkConflictAfterIncentiveChange,
			checkConflictAfterFeatureChange,
			setEssentialConfigurationData,
			setConflict,
		],
	);

	const acceptConflictSolution: ConfiguratorContextType['acceptConflictSolution'] = useCallback(
		async (data) => {
			if (data?.configuredCar?.id) {
				const result = await loadInitialSelection({
					variables: {
						configuredCar: normalizeCarIdentifier(data?.configuredCar?.id),
					},
				});

				if (result.error) {
					console.error(result.error);
					throw new Error('Failed to load data for accepted conflict solution.');
				}

				if (result.data) {
					setDidConfigChange(true);
					setEssentialConfigurationData(result.data.configuredCar);
				}
			}
		},
		[loadInitialSelection, setEssentialConfigurationData],
	);

	const combinedCatalog: Partial<CombinedCatalog> = {
		...catalogOptions,
		...mappedAccessories,
		carInfo,
		trimlines,
		priceSummary: priceSummaryMapped,
	};

	const calculator = windowAvailable() ? (window?.Calculator ?? undefined) : undefined;

	const value: ConfiguratorContextType = {
		carIdentifier,
		setCarIdentifier: async (id) => {
			try {
				const result = await loadInitialSelection({
					variables: {
						configuredCar: id,
					},
				});

				if (result.error) {
					console.error('[setCarIdentifier] Failed to load data for car identifier', result.error);
				}

				if (result.data) {
					setEssentialConfigurationData(result.data.configuredCar);
				}
			} catch (error) {
				console.error('[setCarIdentifier] Unexpected error', error);
			}
		},
		changeConfiguration,
		didConfigChange,
		acceptConflictSolution,
		catalog: combinedCatalog,
		accessories: {
			exclusive: accessoriesExclusive,
			inclusive: accessoriesInclusive,
		},
		loadingFlags: {
			checkingConflicts: isCheckingConflicts,
			catalog: isLoadingCatalog,
			priceSummary: isLoadingPriceSummary || !!financingData?.loading,
			carline: isLoadingCarline,
			accessories: isLoadingAccessories,
		},
		isLoading: initialLoading,
		isItemLoading: (item) => {
			return loadingFeatureIds[item.type][item.featureId]?.loading ?? false;
		},
		isItemChecked: (item) => {
			const clientSideOverwrite = loadingFeatureIds[item.type][item.featureId];

			if (clientSideOverwrite) {
				return clientSideOverwrite.checked;
			}

			if (item.type === 'driveTrain') {
				const modelCode = (item as CatalogItemType['driveTrain']).__item.modelId.code;
				return selection.has(modelCode);
			}

			return selection.has(item.featureId);
		},
		financialCalculator: calculator,
	};
	const [, setHiddenSteps] = useAtom(state.hiddenConfiSteps);
	const combinations = useAtomValue(state.configuredCombinationsAtom);
	const catalogFilled = useMemo(() => {
		const expectedItems = [
			'trimlines',
			'exteriorColor',
			'interiorTiles',
			'accessoriesExclusive',
			'steeringWheels',
			'headlights',
			'rims',
			'packages',
			'equipmentHighlights',
		];
		return expectedItems.every((item) => Object.keys(combinedCatalog).includes(item));
	}, [JSON.stringify(combinedCatalog)]);

	useEffect(() => {
		if (catalogFilled) {
			const hiddenSteps: Record<PossibleConfiguratorOptionValues, boolean> = {
				trimLine: false,
				steeringWheels: false,
				bodyPaint: false,
				paintTypes: false,
				headlights: false,
				rims: false,
				interior: false,
				packages: false,
				equipmentHighlights: false,
				equipmentPackages: false,
				interiorPackages: false,
				paint: false,
				accessoriesAndEquipments: false,
			};

			combinations?.forEach((combination) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				let items: Array<any> | undefined;
				if (combination === 'trimLine') {
					items = combinedCatalog?.trimlines;
				} else if (combination === 'paint') {
					items = combinedCatalog?.exteriorColor;
				} else if (combination === 'interior') {
					items = combinedCatalog?.interiorTiles;
				} else if (combination === 'equipmentPackages') {
					if (!combinedCatalog.accessoriesExclusive) return;
					const _families = combinedCatalog.accessoriesExclusive.flatMap((group) => group);
					const _items = _families.flatMap((f) => f.families.flatMap((itm) => itm.items));
					items = _items.filter((item) => item.family.id === TECHNOLOGY_PACKAGES);
				} else if (combination === 'interiorPackages') {
					if (!accessories || !accessories.configuredCar.accessoriesExclusive) {
						items = [];
					}
					items = accessories?.configuredCar?.accessoriesExclusive?.packagesDetails.data?.filter(
						({ group }) => group.id.toUpperCase() === 'Interior_lines'.toUpperCase(),
					);
				} else if (combination === 'steeringWheels') {
					items = combinedCatalog?.steeringWheels;
				} else if (combination === 'headlights') {
					items = combinedCatalog?.headlights;
				} else if (combination === 'rims') {
					items = combinedCatalog?.rims;
				} else if (combination === 'packages') {
					items = combinedCatalog?.packages;
				} else if (combination === 'equipmentHighlights') {
					items = combinedCatalog?.equipmentHighlights;
				}

				if (items !== undefined && items?.length === 0) {
					hiddenSteps[combination] = true;
				} else if (items?.length && items?.length > 0) {
					hiddenSteps[combination] = false;
				}
			});

			setHiddenSteps(hiddenSteps);
		}
	}, [JSON.stringify(combinedCatalog), JSON.stringify(accessories), catalogFilled]);

	return <ConfiguratorContext.Provider value={value}>{children}</ConfiguratorContext.Provider>;
}
